import { useEffect, useRef, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';

const DEFAULT_TTL_DAYS = 7;
const DEFAULT_DEBOUNCE_MS = 500;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

type StoredDraft<TValues> = {
  values: TValues;
  savedAt: number;
};

export type UseFormDraftAutosaveOptions<TValues extends FieldValues> = {
  form: UseFormReturn<TValues>;
  storageKey: string;
  ttlDays?: number;
  debounceMs?: number;
  /** Disable autosave entirely (e.g. while auth profile is still loading). */
  enabled?: boolean;
};

export type UseFormDraftAutosaveState = {
  hasRecovered: boolean;
  draftAgeMinutes: number | null;
  clearDraft: () => void;
};

/**
 * Persists a react-hook-form draft to localStorage so admins do not lose work
 * on accidental refresh. Restoration runs once on mount; saves are debounced
 * via `form.watch(cb)` to avoid re-rendering the parent on every keystroke.
 *
 * Callers compose `storageKey` with the user id so two admins on a shared
 * workstation cannot see each other's drafts.
 */
export function useFormDraftAutosave<TValues extends FieldValues>(
  options: UseFormDraftAutosaveOptions<TValues>,
): UseFormDraftAutosaveState {
  const {
    form,
    storageKey,
    ttlDays = DEFAULT_TTL_DAYS,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    enabled = true,
  } = options;

  const [recovery, setRecovery] = useState<Omit<UseFormDraftAutosaveState, 'clearDraft'>>({
    hasRecovered: false,
    draftAgeMinutes: null,
  });

  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!enabled || hasMountedRef.current || typeof window === 'undefined') return;
    hasMountedRef.current = true;

    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw) as StoredDraft<TValues>;
      const ageMs = Date.now() - parsed.savedAt;
      if (ageMs > ttlDays * MS_PER_DAY) {
        window.localStorage.removeItem(storageKey);
        return;
      }
      form.reset(parsed.values);
      setRecovery({
        hasRecovered: true,
        draftAgeMinutes: Math.max(0, Math.floor(ageMs / 60000)),
      });
    } catch {
      window.localStorage.removeItem(storageKey);
    }
  }, [enabled, storageKey, ttlDays, form]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const subscription = form.watch(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          const payload: StoredDraft<TValues> = {
            values: form.getValues(),
            savedAt: Date.now(),
          };
          window.localStorage.setItem(storageKey, JSON.stringify(payload));
        } catch {
          // Quota or storage unavailable — silent best-effort.
        }
      }, debounceMs);
    });

    return () => {
      if (timer) clearTimeout(timer);
      subscription.unsubscribe();
    };
  }, [enabled, storageKey, debounceMs, form]);

  const clearDraft = () => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  };

  return { ...recovery, clearDraft };
}
